/** Catalog cover aspect — matches `--listing-media-aspect` (4:3). */
export const LISTING_COVER_ASPECT = 4 / 3

export const LISTING_CROP_OUTPUT = { width: 1080, height: 810 } as const

export interface PhotoCropState {
  /** Multiplier on top of cover-fit scale (1 = fill frame). */
  zoom: number
  /** Offset from viewport center, in viewport pixels. */
  panX: number
  panY: number
}

export function coverScale(imageW: number, imageH: number, viewW: number, viewH: number): number {
  if (imageW < 1 || imageH < 1 || viewW < 1 || viewH < 1) return 1
  return Math.max(viewW / imageW, viewH / imageH)
}

export function containScale(imageW: number, imageH: number, viewW: number, viewH: number): number {
  if (imageW < 1 || imageH < 1 || viewW < 1 || viewH < 1) return 1
  return Math.min(viewW / imageW, viewH / imageH)
}

/** Minimum zoom on top of cover-fit scale (shows full image at this value). */
export function minCropZoom(imageW: number, imageH: number, viewW: number, viewH: number): number {
  const cover = coverScale(imageW, imageH, viewW, viewH)
  if (cover < 1e-9) return 1
  return Math.min(1, containScale(imageW, imageH, viewW, viewH) / cover)
}

export function initialCropState(
  imageW = 0,
  imageH = 0,
  viewW = 0,
  viewH = 0,
): PhotoCropState {
  const zoom = imageW > 0 && viewW > 0 ? minCropZoom(imageW, imageH, viewW, viewH) : 1
  return { zoom, panX: 0, panY: 0 }
}

export function clampPan(
  panX: number,
  panY: number,
  imageW: number,
  imageH: number,
  scale: number,
  viewW: number,
  viewH: number,
): { panX: number; panY: number } {
  const drawW = imageW * scale
  const drawH = imageH * scale
  const maxPanX = Math.max(0, (drawW - viewW) / 2)
  const maxPanY = Math.max(0, (drawH - viewH) / 2)
  return {
    panX: Math.min(maxPanX, Math.max(-maxPanX, panX)),
    panY: Math.min(maxPanY, Math.max(-maxPanY, panY)),
  }
}

export async function loadImageElement(source: File | Blob | string): Promise<HTMLImageElement> {
  const url = typeof source === 'string' ? source : URL.createObjectURL(source)
  try {
    const img = new Image()
    if (typeof source === 'string' && !source.startsWith('blob:')) {
      img.crossOrigin = 'anonymous'
    }
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve()
      img.onerror = () => reject(new Error('Nepavyko užkrauti nuotraukos'))
      img.src = url
    })
    return img
  } finally {
    if (typeof source !== 'string') URL.revokeObjectURL(url)
  }
}

export async function exportCroppedListingPhoto(
  img: HTMLImageElement,
  state: PhotoCropState,
  viewport: { width: number; height: number },
  output = LISTING_CROP_OUTPUT,
): Promise<Blob> {
  const iw = img.naturalWidth
  const ih = img.naturalHeight
  const baseScale = coverScale(iw, ih, viewport.width, viewport.height)
  const scale = baseScale * state.zoom
  const { panX, panY } = clampPan(state.panX, state.panY, iw, ih, scale, viewport.width, viewport.height)

  const canvas = document.createElement('canvas')
  canvas.width = output.width
  canvas.height = output.height
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas nepalaikomas')

  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  const drawW = iw * scale
  const drawH = ih * scale
  const cx = viewport.width / 2 + panX
  const cy = viewport.height / 2 + panY
  const dx = cx - drawW / 2
  const dy = cy - drawH / 2
  const k = output.width / viewport.width

  ctx.drawImage(img, dx * k, dy * k, drawW * k, drawH * k)

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Nepavyko sugeneruoti nuotraukos'))),
      'image/jpeg',
      0.9,
    )
  })
}

export function croppedFileName(originalName: string): string {
  const base = originalName.replace(/\.[^.]+$/, '') || 'photo'
  return `${base}.jpg`
}
